// src/components/circuit/TrackCanvas.jsx
// Canvas di anteprima del circuito (card + dettaglio a pagina intera). Disegna:
//  - circuiti "legacy": polilinea centerline spessa (come prima)
//  - circuiti "manuali": banda piena fra bordo sx/dx + cordoli + nomi curva
// Settori S1/S2/S3 (customSectors), area box e linea start restano supportati.
// TrackCanvas è responsive: si adatta al contenitore mantenendo l'aspect ratio
// (niente più stretch).
// I colori del canvas vengono letti dai token del design system (--rs-canvas-*,
// --rs-red, --rs-lime, --rs-warning) via getComputedStyle: la geometria non cambia.
import React, { useRef, useEffect } from 'react';
import { projectLatLonToXY, fitToCanvas } from './geometry';

/* ===== Palette canvas dai token CSS ===== */
function readPalette(el) {
    const cs = el ? getComputedStyle(el) : null;
    const v = (name, fallback) => {
        const val = cs ? cs.getPropertyValue(name).trim() : '';
        return val || fallback;
    };
    return {
        track: v('--rs-canvas-track', 'rgba(84, 88, 96, 0.95)'),
        outline: v('--rs-canvas-outline', 'rgba(255, 255, 255, 0.10)'),
        text: v('--rs-canvas-text', '#ffffff'),
        textShadow: v('--rs-canvas-text-shadow', 'rgba(0, 0, 0, 0.8)'),
        red: v('--rs-red', '#e10600'),
        lime: v('--rs-lime', '#c0ff03'),
        warning: v('--rs-warning', '#f59e0b'),
        border: v('--rs-border-strong', 'rgba(255, 255, 255, 0.22)'),
        mono: v('--font-mono', 'monospace'),
    };
}

/* ===== Mini preview per le card lista ===== */
export function MiniTrack({ points, leftBorder, rightBorder, className = '' }) {
    const ref = useRef(null);
    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const W = canvas.width, H = canvas.height;
        const pal = readPalette(canvas);
        ctx.clearRect(0, 0, W, H);

        const hasBorders = Array.isArray(leftBorder) && leftBorder.length > 2
            && Array.isArray(rightBorder) && rightBorder.length > 2;

        if (!hasBorders && (!points || points.length < 2)) {
            ctx.setLineDash([6, 6]);
            ctx.strokeStyle = pal.border;
            ctx.lineWidth = 1.5;
            ctx.strokeRect(14, 14, W - 28, H - 28);
            ctx.setLineDash([]);
            return;
        }

        const drawFinish = (a, b) => {
            if (!a || !b) return;
            const ang = Math.atan2(b.Y - a.Y, b.X - a.X);
            ctx.save();
            ctx.translate(a.X, a.Y);
            ctx.rotate(ang);
            ctx.strokeStyle = pal.text;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(0, -7);
            ctx.lineTo(0, 7);
            ctx.stroke();
            ctx.restore();
        };

        if (hasBorders) {
            const all = [...leftBorder, ...rightBorder];
            const fitted = fitToCanvas(projectLatLonToXY(all), W, H, 14);
            const lf = fitted.slice(0, leftBorder.length);
            const rf = fitted.slice(leftBorder.length);
            ctx.beginPath();
            lf.forEach((p, i) => (i ? ctx.lineTo(p.X, p.Y) : ctx.moveTo(p.X, p.Y)));
            for (let i = rf.length - 1; i >= 0; i--) ctx.lineTo(rf[i].X, rf[i].Y);
            ctx.closePath();
            ctx.fillStyle = pal.track;
            ctx.fill();
            ctx.strokeStyle = pal.outline;
            ctx.lineWidth = 1;
            ctx.stroke();
            drawFinish(lf[0], lf[1]);
            return;
        }

        const local = projectLatLonToXY(points);
        const fitted = fitToCanvas(local, W, H, 14);
        // Bordo pista
        ctx.beginPath();
        fitted.forEach((p, i) => (i ? ctx.lineTo(p.X, p.Y) : ctx.moveTo(p.X, p.Y)));
        ctx.strokeStyle = pal.outline;
        ctx.lineWidth = 9;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
        // Asfalto
        ctx.beginPath();
        fitted.forEach((p, i) => (i ? ctx.lineTo(p.X, p.Y) : ctx.moveTo(p.X, p.Y)));
        ctx.strokeStyle = pal.track;
        ctx.lineWidth = 7;
        ctx.stroke();
        // Centerline
        ctx.beginPath();
        fitted.forEach((p, i) => (i ? ctx.lineTo(p.X, p.Y) : ctx.moveTo(p.X, p.Y)));
        ctx.strokeStyle = pal.red;
        ctx.lineWidth = 1.25;
        ctx.globalAlpha = 0.85;
        ctx.stroke();
        ctx.globalAlpha = 1;
        drawFinish(fitted[0], fitted[1]);
    }, [points, leftBorder, rightBorder]);
    return (
        <canvas ref={ref} width={320} height={140} className={`circ-mini ${className}`} aria-hidden="true" />
    );
}

/* ===== Disegno completo (in coordinate CSS px) ===== */
function drawTrack(ctx, W, H, pal, { points, sectors, pitboxArea, leftBorder, rightBorder, kerbs, corners }) {
    const hasBorders = Array.isArray(leftBorder) && leftBorder.length > 2
        && Array.isArray(rightBorder) && rightBorder.length > 2;
    if ((!points || points.length < 2) && !hasBorders) return;

    const allPoints = [];
    if (points) points.forEach(p => allPoints.push({ lat: p.lat, lon: p.lon }));
    if (hasBorders) {
        leftBorder.forEach(p => allPoints.push({ lat: p.lat, lon: p.lon }));
        rightBorder.forEach(p => allPoints.push({ lat: p.lat, lon: p.lon }));
    }
    if (pitboxArea && pitboxArea.length > 0) pitboxArea.forEach(p => allPoints.push({ lat: p.lat, lon: p.lon }));

    const fitted = fitToCanvas(projectLatLonToXY(allPoints), W, H, 32);

    // Proietta un singolo punto lat/lon nello stesso frame (per cordoli)
    const projectOne = (pt) => {
        const all = [...allPoints, pt];
        const f = fitToCanvas(projectLatLonToXY(all), W, H, 32);
        return f[f.length - 1];
    };

    let cursor = 0;
    const trackFitted = points ? fitted.slice(cursor, cursor + points.length) : [];
    cursor += points ? points.length : 0;
    const leftFitted = hasBorders ? fitted.slice(cursor, cursor + leftBorder.length) : [];
    cursor += hasBorders ? leftBorder.length : 0;
    const rightFitted = hasBorders ? fitted.slice(cursor, cursor + rightBorder.length) : [];
    cursor += hasBorders ? rightBorder.length : 0;
    const boxFitted = (pitboxArea && pitboxArea.length) ? fitted.slice(cursor, cursor + pitboxArea.length) : [];

    // Area box
    if (boxFitted.length >= 3) {
        ctx.beginPath();
        boxFitted.forEach((p, i) => (i ? ctx.lineTo(p.X, p.Y) : ctx.moveTo(p.X, p.Y)));
        ctx.closePath();
        ctx.globalAlpha = 0.14;
        ctx.fillStyle = pal.warning;
        ctx.fill();
        ctx.globalAlpha = 0.7;
        ctx.strokeStyle = pal.warning;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
        const cx = boxFitted.reduce((s, p) => s + p.X, 0) / boxFitted.length;
        const cy = boxFitted.reduce((s, p) => s + p.Y, 0) / boxFitted.length;
        ctx.font = `600 10px ${pal.mono}`;
        ctx.fillStyle = pal.warning;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('BOX', cx, cy);
    }

    if (hasBorders) {
        ctx.beginPath();
        leftFitted.forEach((p, i) => (i ? ctx.lineTo(p.X, p.Y) : ctx.moveTo(p.X, p.Y)));
        for (let i = rightFitted.length - 1; i >= 0; i--) ctx.lineTo(rightFitted[i].X, rightFitted[i].Y);
        ctx.closePath();
        ctx.fillStyle = pal.track;
        ctx.fill();
        const drawBorder = (arr) => {
            ctx.beginPath();
            arr.forEach((p, i) => (i ? ctx.lineTo(p.X, p.Y) : ctx.moveTo(p.X, p.Y)));
            ctx.closePath();
            ctx.strokeStyle = pal.text;
            ctx.globalAlpha = 0.5;
            ctx.lineWidth = 1.25;
            ctx.stroke();
            ctx.globalAlpha = 1;
        };
        drawBorder(leftFitted);
        drawBorder(rightFitted);
    } else {
        if (sectors && sectors.length > 0 && trackFitted.length) {
            sectors.forEach(sector => {
                const sp = trackFitted.slice(sector.startIdx, sector.endIdx + 1);
                if (sp.length < 2) return;
                ctx.beginPath();
                sp.forEach((p, i) => (i ? ctx.lineTo(p.X, p.Y) : ctx.moveTo(p.X, p.Y)));
                ctx.strokeStyle = sector.color;
                ctx.lineWidth = 14;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.globalAlpha = 0.35;
                ctx.stroke();
                ctx.globalAlpha = 1;
            });
        }
        ctx.beginPath();
        trackFitted.forEach((p, i) => (i ? ctx.lineTo(p.X, p.Y) : ctx.moveTo(p.X, p.Y)));
        ctx.strokeStyle = pal.outline;
        ctx.lineWidth = 12;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
        ctx.beginPath();
        trackFitted.forEach((p, i) => (i ? ctx.lineTo(p.X, p.Y) : ctx.moveTo(p.X, p.Y)));
        ctx.strokeStyle = pal.track;
        ctx.lineWidth = 10;
        ctx.stroke();
    }

    // Centerline
    if (trackFitted.length > 1) {
        ctx.beginPath();
        trackFitted.forEach((p, i) => (i ? ctx.lineTo(p.X, p.Y) : ctx.moveTo(p.X, p.Y)));
        ctx.strokeStyle = pal.red;
        ctx.globalAlpha = hasBorders ? 0.55 : 0.85;
        ctx.lineWidth = hasBorders ? 1.5 : 2;
        ctx.setLineDash(hasBorders ? [5, 5] : []);
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.setLineDash([]);
    }

    // Settori S1/S2/S3 sopra la banda
    if (hasBorders && sectors && sectors.length > 0 && trackFitted.length) {
        sectors.forEach(sector => {
            const sp = trackFitted.slice(sector.startIdx, sector.endIdx + 1);
            if (sp.length < 2) return;
            ctx.beginPath();
            sp.forEach((p, i) => (i ? ctx.lineTo(p.X, p.Y) : ctx.moveTo(p.X, p.Y)));
            ctx.strokeStyle = sector.color;
            ctx.lineWidth = 4;
            ctx.lineCap = 'round';
            ctx.globalAlpha = 0.7;
            ctx.stroke();
            ctx.globalAlpha = 1;
        });
    }

    // Cordoli (rosso/bianco), supporta loop chiusi
    if (Array.isArray(kerbs) && kerbs.length) {
        kerbs.forEach(kerb => {
            const kp = (kerb.points || []).map(projectOne).filter(Boolean);
            if (kp.length < 2) return;
            const seq = kerb.closed ? [...kp, kp[0]] : kp;
            for (let i = 1; i < seq.length; i++) {
                ctx.beginPath();
                ctx.moveTo(seq[i - 1].X, seq[i - 1].Y);
                ctx.lineTo(seq[i].X, seq[i].Y);
                ctx.strokeStyle = i % 2 === 0 ? pal.red : pal.text;
                ctx.lineWidth = 5;
                ctx.lineCap = 'butt';
                ctx.stroke();
            }
        });
    }

    // Nomi curva
    if (Array.isArray(corners) && corners.length && trackFitted.length) {
        ctx.font = `600 10px ${pal.mono}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        corners.forEach(c => {
            const mid = (c.startIdx <= c.endIdx) ? Math.floor((c.startIdx + c.endIdx) / 2) : c.startIdx;
            const p = trackFitted[mid];
            if (!p) return;
            const label = c.name || 'Curva';
            const w = ctx.measureText(label).width + 10;
            ctx.fillStyle = pal.textShadow;
            ctx.fillRect(p.X - w / 2, p.Y - 9, w, 18);
            ctx.fillStyle = pal.lime;
            ctx.fillText(label, p.X, p.Y);
        });
    }

    // Linea start
    const startArr = trackFitted.length ? trackFitted : leftFitted;
    if (startArr.length > 1) {
        const s0 = startArr[0], s1 = startArr[1] || startArr[0];
        const ang = Math.atan2(s1.Y - s0.Y, s1.X - s0.X);
        ctx.save();
        ctx.translate(s0.X, s0.Y);
        ctx.rotate(ang);
        ctx.strokeStyle = pal.text;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(0, -12);
        ctx.lineTo(0, 12);
        ctx.stroke();
        ctx.restore();
    }
}

/* ===== Canvas grande responsive ===== */
export function TrackCanvas({
    points, sectors, pitboxArea,
    leftBorder, rightBorder, kerbs, corners,
    responsive = false, width = 580, height = 480,
}) {
    const wrapRef = useRef(null);
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        const wrap = wrapRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;

        const render = () => {
            let w = width, h = height;
            if (responsive && wrap) {
                w = wrap.clientWidth || width;
                h = wrap.clientHeight || height;
            }
            if (w < 2 || h < 2) return;
            canvas.width = Math.floor(w * dpr);
            canvas.height = Math.floor(h * dpr);
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.clearRect(0, 0, w, h);
            drawTrack(ctx, w, h, readPalette(canvas), { points, sectors, pitboxArea, leftBorder, rightBorder, kerbs, corners });
        };

        render();
        let ro = null;
        if (responsive && wrap && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(render);
            ro.observe(wrap);
        }
        return () => { if (ro) ro.disconnect(); };
    }, [points, sectors, pitboxArea, leftBorder, rightBorder, kerbs, corners, responsive, width, height]);

    return (
        <div
            ref={wrapRef}
            className="circ-canvas-wrap"
            style={responsive ? { width: '100%', height: '100%' } : { width, height }}
        >
            <canvas ref={ref} className="circ-canvas" />
        </div>
    );
}
